import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { onAnimationEnd } from "@theme/utilities";
import { StandardEvents } from "@shopify/events";
import { applyLocalPriceToContainer } from "./tc-local-variants.js?v=20261023-instant-1";
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    "stickyBar",
    "addToCartButton",
    "quantityDisplay",
    "quantityNumber",
  ];
  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #resetTimeout;
  #isStuck = !1;
  #animationTimeout = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #hiddenByBottom = !1;
  #selection = 0;
  #availableButtonLabel;
  #availableButtonIcon;
  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted)
      this.#abortController = new AbortController();
    this.#rememberAvailableButton();
    this.#setupIntersectionObserver();
    const { signal } = this.#abortController;
    (this.closest(".shopify-section")?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal },
    ),
      document.addEventListener(
        StandardEvents.cartLinesUpdate,
        this.#handleCartAddComplete,
        { signal },
      ),
      document.addEventListener(
        StandardEvents.cartError,
        this.#handleCartAddComplete,
        { signal },
      ),
      document.addEventListener(
        ThemeEvents.quantitySelectorUpdate,
        this.#handleQuantityUpdate,
        { signal },
      ),
      this.#getInitialQuantity(),
      customElements.whenDefined("shopify-chat").then(() => {
        signal.aborted ||
          (this.#isStuck && this.#isChatActive() && this.#hideStickyBar());
      }));
  }
  disconnectedCallback() {
    (this.#selection++,
      super.disconnectedCallback(),
      this.#buyButtonsIntersectionObserver?.disconnect(),
      this.#mainBottomObserver?.disconnect(),
      this.#abortController.abort(),
      this.#animationTimeout && clearTimeout(this.#animationTimeout));
  }
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    const buyButtonsBlock = productForm.closest(".buy-buttons-block");
    if (!buyButtonsBlock) return;
    const footer =
      document.querySelector("footer") ??
      document.querySelector('[class*="footer-group"]');
    footer &&
      ((this.#buyButtonsIntersectionObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry)
            if (!entry.isIntersecting && !this.#isStuck) {
              const rect = entry.target.getBoundingClientRect();
              if (rect.bottom < 0 || rect.top < 0) {
                if (this.#isChatActive()) return;
                this.#showStickyBar();
              }
            } else
              entry.isIntersecting &&
                this.#isStuck &&
                ((this.#hiddenByBottom = !1), this.#hideStickyBar());
        },
      )),
      (this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry) {
            if (entry.isIntersecting && this.#isStuck)
              ((this.#hiddenByBottom = !0), this.#hideStickyBar());
            else if (!entry.isIntersecting && this.#hiddenByBottom) {
              const rect = buyButtonsBlock.getBoundingClientRect();
              (rect.bottom < 0 || rect.top < 0) &&
                ((this.#hiddenByBottom = !1),
                this.#isChatActive() || this.#showStickyBar());
            }
          }
        },
        { rootMargin: "200px 0px 0px 0px" },
      )),
      this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock),
      this.#mainBottomObserver.observe(footer),
      (this.#targetAddToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]',
      )));
  }
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    ((this.#targetAddToCartButton.dataset.puppet = "true"),
      this.#targetAddToCartButton.click());
    const cartIcon = document.querySelector(".header-actions__cart-icon");
    if (
      (this.refs.addToCartButton.dataset.added !== "true" &&
        (this.refs.addToCartButton.dataset.added = "true"),
      !cartIcon || !this.refs.addToCartButton || !this.refs.productImage)
    )
      return;
    this.#resetTimeout && clearTimeout(this.#resetTimeout);
    const flyToCartElement = document.createElement("fly-to-cart"),
      sourceStyles = getComputedStyle(this.refs.productImage);
    (flyToCartElement.classList.add("fly-to-cart--sticky"),
      flyToCartElement.style.setProperty(
        "background-image",
        `url(${this.refs.productImage.src})`,
      ),
      (flyToCartElement.useSourceSize = "true"),
      (flyToCartElement.source = this.refs.productImage),
      (flyToCartElement.destination = cartIcon),
      document.body.appendChild(flyToCartElement),
      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]),
      (this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute("data-added");
      }, 800)));
  };
  #rememberAvailableButton() {
    const button = this.refs.addToCartButton;
    if (!button || button.disabled) return;
    const label = button.querySelector(
      ".add-to-cart-text__content > span:first-child",
    );
    if (label) this.#availableButtonLabel = label.textContent;
    this.#availableButtonIcon = button
      .querySelector(".add-to-cart-text > .svg-wrapper")
      ?.cloneNode(true);
  }
  #applyLocalVariant(variant) {
    if (!variant) return false;
    const title = this.querySelector(".sticky-add-to-cart__variant");
    const nextTitle = variant.title || variant.options.join(" / ");
    if (title && title.textContent.trim() !== nextTitle)
      title.textContent = nextTitle;
    applyLocalPriceToContainer(
      this.querySelector('[ref="priceContainer"]'),
      variant,
    );
    const image = this.refs.productImage;
    if (
      image &&
      variant.image?.src &&
      image.getAttribute("src") !== variant.image.src
    ) {
      // A stale srcset would override the newly selected image's src.
      image.removeAttribute("srcset");
      image.setAttribute("src", variant.image.src);
    }
    this.dataset.currentVariantId = String(variant.id);
    this.dataset.variantAvailable = String(variant.available);
    const button = this.refs.addToCartButton;
    if (button.disabled && this.#availableButtonLabel) {
      const label = button.querySelector(
        ".add-to-cart-text__content > span:first-child",
      );
      const content = button.querySelector(".add-to-cart-text");
      if (label && content) {
        label.textContent = this.#availableButtonLabel;
        if (
          this.#availableButtonIcon &&
          !content.querySelector(":scope > .svg-wrapper")
        ) {
          content.prepend(this.#availableButtonIcon.cloneNode(true));
        }
        button.disabled = false;
      }
    }
    this.#updateButtonText();
    return true;
  }
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest("product-card") ||
      event.target.closest(".shopify-section, dialog") !==
        this.closest(".shopify-section") ||
      !event.promise ||
      typeof event.promise.then !== "function"
    )
      return;
    const picker = event.target.closest("variant-picker");
    if (
      picker?.dataset.productId &&
      String(picker.dataset.productId) !== String(this.dataset.productId)
    )
      return;
    const selection = ++this.#selection;
    const local = picker?.getLocalVariant?.();
    if (!this.#applyLocalVariant(local)) {
      // An option-value ID is not a cart variant ID. Wait for the native
      // response for sold-out, incomplete or special pricing combinations.
      this.dataset.currentVariantId = "";
      this.refs.addToCartButton.disabled = true;
      this.#handleVariantUnavailable();
    }
    event.promise
      .then(({ detail }) => {
        if (selection !== this.#selection || !this.isConnected || !detail?.html)
          return;
        const { html, productId, resource: variant } = detail;
        if (productId && productId !== this.dataset.productId) return;
        const newStickyAddToCart = [
          ...html.querySelectorAll("sticky-add-to-cart"),
        ].find(
          (node) =>
            String(node.dataset.productId) === String(this.dataset.productId),
        );
        if (!newStickyAddToCart) return;
        const newStickyBar =
          newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;
        const currentStuck =
            this.refs.stickyBar.getAttribute("data-stuck") || "false",
          variantAvailable = newStickyAddToCart.dataset.variantAvailable,
          added = this.refs.addToCartButton.dataset.added;
        (morph(this.refs.stickyBar, newStickyBar, { childrenOnly: !0 }),
          this.refs.stickyBar.setAttribute("data-stuck", currentStuck),
          (this.dataset.variantAvailable = variantAvailable),
          variant &&
            variant.id &&
            (this.dataset.currentVariantId = variant.id));
        if (added === "true") this.refs.addToCartButton.dataset.added = added;
        this.#rememberAvailableButton();
        const productForm = this.#getProductForm();
        (productForm &&
          (this.#targetAddToCartButton = productForm.querySelector(
            '[ref="addToCartButton"]',
          )),
          variant == null && this.#handleVariantUnavailable(),
          this.#updateButtonText());
      })
      .catch((error) => {
        error?.name !== "AbortError" &&
          console.warn("[sticky-add-to-cart] Event promise rejected:", error);
      });
  };
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(
        ".sticky-add-to-cart__variant",
      ),
      productId = this.dataset.productId,
      variantPicker = this.closest(".shopify-section")?.querySelector(
        `variant-picker[data-product-id="${productId}"]`,
      );
    if (!variantTitleElement || !variantPicker) return;
    const selectedOptions = Array.from(
      variantPicker.querySelectorAll(
        "select option:checked, fieldset input:checked",
      ),
    )
      .map((option) => option.value)
      .filter((value) => value !== "")
      .join(" / ");
    selectedOptions && (variantTitleElement.textContent = selectedOptions);
  };
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      this.#targetAddToCartButton &&
        (this.#targetAddToCartButton.dataset.puppet = "false");
    };
    "promise" in event && event.promise instanceof Promise
      ? event.promise.finally(resetPuppet)
      : resetPuppet();
  };
  #handleQuantityUpdate = (event) => {
    event.detail.cartLine ||
      ((this.#currentQuantity = event.detail.quantity),
      this.#updateButtonText());
  };
  #showStickyBar() {
    const { stickyBar } = this.refs;
    ((this.#isStuck = !0), (stickyBar.dataset.stuck = "true"));
  }
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    ((this.#isStuck = !1), (stickyBar.dataset.stuck = "false"));
  }
  #isChatActive() {
    return customElements.get("shopify-chat")
      ? !!document.querySelector("shopify-chat")
      : !1;
  }
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;
    const sectionId = sectionElement.id.replace("shopify-section-", "");
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
    );
  }
  #getInitialQuantity() {
    ((this.#currentQuantity =
      parseInt(this.dataset.initialQuantity || "1") || 1),
      this.#updateButtonText());
  }
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs,
      available = !addToCartButton.disabled;
    ((quantityNumber.textContent = this.#currentQuantity.toString()),
      available && this.#currentQuantity > 1
        ? (quantityDisplay.style.display = "inline")
        : (quantityDisplay.style.display = "none"));
  }
}
customElements.get("sticky-add-to-cart") ||
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
